/**
 * Synthesizes SquadPlayer rows for draft "Sign" actions on truly external
 * players (not already in the roster/targets data). Shared by the Depth
 * panel's tier grouping and the Position Map's GPR pool (SE-5349) so the
 * two surfaces never disagree on a signed player's contribution.
 * @module features/squad-planning/utils/buildDraftSignedPlayers
 */

import type { SignedPlayerData } from "@/features/squad-planning/stores/scenarioStore";
import type {
  ActionDetails,
  PlayerAction,
  SquadPlayer,
} from "@/features/squad-planning/types";

/**
 * Builds synthetic First Team SquadPlayer entries from the planning-mode
 * draft's Sign actions.
 * @param draftActions - Draft action map keyed by player id
 * @param draftSignedPlayers - Signed-player display data keyed by player id
 * @param draftDetails - Draft financial details keyed by player id
 * @param knownPlayerIds - Ids already present in roster/targets data (skipped)
 * @returns Synthetic SquadPlayer rows for external signed players
 */
export function buildDraftSignedPlayers(
  draftActions: Readonly<Record<string, PlayerAction>>,
  draftSignedPlayers: Readonly<Record<string, SignedPlayerData>>,
  draftDetails: Readonly<Record<string, ActionDetails>>,
  knownPlayerIds: ReadonlySet<string>
): readonly SquadPlayer[] {
  return Object.entries(draftActions)
    .filter(([id, action]) => action === "Sign" && !knownPlayerIds.has(id))
    .map(([id]) => {
      const data = draftSignedPlayers[id];
      const details = draftDetails[id];
      const posAbbrs = data?.positions
        ? data.positions.split(",").map(s => s.trim())
        : [];
      return {
        id,
        displayName: data?.displayName ?? "Unknown",
        age: data?.age ?? null,
        gprScore: data?.gprScore ?? null,
        wage: details?.annualSalary ?? null,
        estValuation: details?.transferFee ?? null,
        readinessTag: null,
        isAcademy: false,
        tierName: "First Team" as const,
        positionAbbreviations: posAbbrs,
        teamName: data?.teamName ?? null,
        nationalityName: data?.nationalityName ?? null,
        primaryPosition: posAbbrs[0] ?? null,
        minutesPlayed: null,
        contractExpires: null,
        isInjured: false,
        status: null,
        fairFee: null,
        expectedFee: null,
        gpv: null,
        gpvMin: null,
        gpvMax: null,
        watchlistCount: 0,
        transitionType: null,
      };
    });
}
